import React, { useState } from "react";
import PropTypes from "prop-types";

import QuickCaptureBtn from "../QuickCaptureBtn";
import Leaderboard from "./Leaderboard";
import { LANE_COLORS, withArticle } from "./render";
import {
  challengeUrl, downloadBlob, redditUrl, scoreCardBlob, shareText, shareTitle, xUrl,
} from "./share";

function canShareNatively() {
  return typeof navigator !== "undefined" && typeof navigator.share === "function";
}

// The end of a run: the score, ways to share it, today's board, and the nouns
// you missed with a way to keep them. In that order - the share buttons are
// the point while the score is still exciting.
export default function GameOver({
  articles,
  best,
  board,
  isSignedIn,
  onChangeDeck,
  onRestart,
  quickCapture,
  result,
  saveState,
  shareUrl,
  signInUrl,
  signUpUrl,
  title,
}) {
  const [copied, setCopied] = useState(false);
  const url = challengeUrl(shareUrl, result.score);
  // The run plus what it was, for the text, the title and the card.
  const run = { ...result, articles, title };
  const fileStem = title.toLowerCase().replace(/[^a-z]+/g, "-");
  const isNewBest = result.score > 0 && result.score >= best;

  const copy = () => {
    const text = shareText(run, url);
    const done = () => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    };

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done, () => window.prompt("Copy your result:", text));
    } else {
      window.prompt("Copy your result:", text);
    }
  };

  // On a phone this hands the card image and the text to whatever app they
  // pick - which is how a screenshot-style post actually gets made.
  const shareNatively = async () => {
    const text = shareText(run, url);
    try {
      const blob = await scoreCardBlob(run);
      const file = blob && new File([blob], `${fileStem}.png`, { type: "image/png" });
      if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text, title: shareTitle(run) });
      } else {
        await navigator.share({ text, title: shareTitle(run), url });
      }
    } catch (e) {
      // Cancelled from the share sheet - nothing to do.
    }
  };

  const saveImage = async () => {
    const blob = await scoreCardBlob(run);
    if (blob) downloadBlob(blob, `${fileStem}-${result.score}.png`);
  };

  return (
    <div className="gar-card gar-card-wide">
      <div className="gar-title">Game over</div>
      <div className="gar-final">{result.score}</div>
      <p className="gar-text">
        {result.correct} right · {result.bestStreak} in a row
        {isNewBest ? " · new best!" : ` · best ${best}`}
      </p>

      <div className="gar-share">
        {canShareNatively() && (
          <button className="gar-share-btn" onClick={shareNatively} type="button">
            Share
          </button>
        )}
        <a className="gar-share-btn gar-share-reddit" href={redditUrl(run, url)} rel="noopener noreferrer" target="_blank">
          Reddit
        </a>
        <a className="gar-share-btn" href={xUrl(run, url)} rel="noopener noreferrer" target="_blank">
          X
        </a>
        <button className="gar-share-btn" onClick={copy} type="button">
          {copied ? "Copied!" : "Copy"}
        </button>
        <button className="gar-share-btn" onClick={saveImage} type="button">
          Image
        </button>
      </div>

      <button className="gar-go" onClick={onRestart} type="button">
        Run again
      </button>

      <div className="gar-section">
        <div className="gar-section-title">Today’s leaderboard</div>
        {!isSignedIn && result.score > 0 && (
          <p className="gar-save-prompt">
            <a className="gar-save-cta" href={signUpUrl}>
              Sign up free
            </a>{" "}
            to put {result.score} on today’s board, or{" "}
            <a href={signInUrl}>log in</a>. We’ll hold onto it until midnight UTC.
          </p>
        )}
        {isSignedIn && saveState === "saving" && <p className="gar-board-note">Saving your score…</p>}
        {isSignedIn && saveState === "error" && (
          <p className="gar-board-note">Couldn’t save that score. Your next run will try again.</p>
        )}
        <Leaderboard board={board} />
      </div>

      {result.missed.length > 0 && (
        <div className="gar-section">
          <div className="gar-section-title">Missed</div>
          <ul className="gar-missed">
            {result.missed.map((noun) => (
              <li key={noun.word}>
                <span>
                  <strong style={{ color: LANE_COLORS[noun.article] }}>{withArticle(noun)}</strong>
                  {noun.gloss && <span className="gar-missed-gloss"> - {noun.gloss}</span>}
                </span>
                {quickCapture && (
                  // Remount per noun, so each opens on its own word.
                  <QuickCaptureBtn
                    autoSubmitInitialText
                    className="gar-capture"
                    collectionsUrl={quickCapture.collectionsUrl}
                    demoEntries={quickCapture.demoEntries}
                    entriesUrl={quickCapture.entriesUrl}
                    importsUrl={quickCapture.importsUrl}
                    initialText={noun.written}
                    isPro={quickCapture.isPro}
                    isSignedIn={isSignedIn}
                    key={noun.word}
                    signInUrl={signInUrl}
                    signUpUrl={signUpUrl}
                    targetLanguageEnglishName={quickCapture.targetLanguageEnglishName}
                    targetLanguageIso={quickCapture.targetLanguageIso}
                    targetLanguageName={quickCapture.targetLanguageName}
                    text={false}
                    title={`Quick Capture "${noun.written}" to review it later`}
                  />
                )}
              </li>
            ))}
          </ul>
          {quickCapture && (
            <p className="gar-board-note">
              <span className="glyphicon glyphicon-flash" /> Quick Capture a missed noun to review it in
              Clozemaster.
            </p>
          )}
        </div>
      )}

      <button className="gar-link" onClick={onChangeDeck} type="button">
        Change nouns
      </button>
    </div>
  );
}

GameOver.defaultProps = { board: null, quickCapture: null, saveState: null };

GameOver.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.string).isRequired,
  best: PropTypes.number.isRequired,
  board: PropTypes.object,
  isSignedIn: PropTypes.bool.isRequired,
  onChangeDeck: PropTypes.func.isRequired,
  onRestart: PropTypes.func.isRequired,
  quickCapture: PropTypes.object,
  result: PropTypes.shape({
    bestStreak: PropTypes.number,
    correct: PropTypes.number,
    history: PropTypes.arrayOf(PropTypes.string),
    missed: PropTypes.arrayOf(PropTypes.object),
    score: PropTypes.number,
  }).isRequired,
  saveState: PropTypes.string,
  shareUrl: PropTypes.string.isRequired,
  signInUrl: PropTypes.string.isRequired,
  signUpUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
};
